/* eslint-disable @typescript-eslint/no-unused-vars */
import { inject, observer } from 'mobx-react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import { useMedia } from 'react-use';
import { priceListItemsActions, PriceListItemsStore, PriceListItem } from '../store/priceListItemsStore';
import { PriceListItemCalculateComplexTableInterface } from '../api/priceListApi';
import { COMPLEX_PRICE_VENDOR_CODES, COMPLEX_VENDOR_CODE_SET } from '../constants/complexPriceVendorCodes';
import { complexRowHoverStore } from '../store/complexRowHoverStore';
import { SpinnerOverlay } from 'modules/core/components/spinner';
import { Button } from 'modules/core/components/buttons';
import { TYPE_OPERATION } from 'modules/core/constants/ref-values';

interface PriceListViewComplexTableProps {
  priceListItemsStore?: PriceListItemsStore;
  priceListId: string;
  vehicleTypeRefValueName?: string;
  /** Готовые данные (просмотр прайса офиса): если переданы — сами не грузим,
   *  чтобы не писать в общий стор priceListItemsStore */
  items?: PriceListItemCalculateComplexTableInterface[];
}

// Стили, адаптированные из PriceListEditTableV3 для единообразного вида.
// table-layout: fixed — ширины столбцов из шапки, как у «Розничных цен»
const TABLE_STYLES = `
  .price-list-table { margin-bottom: 0; table-layout: fixed; }
  .price-list-table td, .price-list-table th {
    padding: 0 !important;
    line-height: 1.1;
    vertical-align: middle;
  }
  .price-list-table thead th {
    height: 50px !important;
    vertical-align: middle !important;
    text-align: center !important;
    padding: 0.75rem 0.5rem !important;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: #e9ecef;
    font-size: 0.95rem;
  }
  .price-list-table tbody tr:hover td:first-child { background-color: #f1f3f5; }
  .price-list-table td.position-cell {
    padding: 0.4rem 0.6rem !important;
    background-color: #fafbfc;
  }
  /* Ячейки цен: значениям с бэка задаются на расчёте, руками их менять нельзя —
     серый фон только для чтения, как у рассчитанных ячеек в ComplexCalculationTable ($gray-100) */
  .price-list-table td.calc-cell {
    padding: 0.4rem 0.6rem !important;
    background-color: #f8f9fa;
    text-align: center;
  }
  /* Изменившаяся цена комплекса (правка строки этого комплекса в «Розничных
     ценах»): info-заливка #e7f1ff (как бейджи диаметров в PriceListEditTableV3),
     новое значение — жирным, прежнее — зачёркнуто серым мелко. Правило после
     .calc-cell: специфичность одинаковая, побеждает поздний */
  .price-list-table td.complex-price-changed { background-color: #e7f1ff; }
  .price-list-table td.complex-price-changed .complex-price-old {
    display: inline-block;
    margin-left: 0.35rem;
    font-size: 0.8em;
  }
  /* Hover-связка: строка комплекса, чью позицию в «Розничных ценах» сейчас
     держит курсор. Заливка — та же info-синяя, что у изменившихся ячеек;
     акцентная полоса слева отличает связанную строку. Правило после
     .position-cell/.calc-cell — та же специфичность, побеждает поздний;
     при прямом hover на строке срабатывает более специфичный tr:hover */
  .price-list-table tr.complex-row-linked td { background-color: #e7f1ff; }
  .price-list-table tr.complex-row-linked td:first-child { box-shadow: inset 3px 0 0 0 #0b5ed7; }
  /* Планшет/телефон (≤991px): матрица не сжимается, а скроллится пальцем внутри
     .table-responsive. Первая колонка «замораживается» (sticky left) — комплекс
     всегда виден при горизонтальном скролле по диаметрам.
     border-collapse: separate обязателен: при collapse Chrome не переносит границы
     sticky-ячеек; чтобы сетка не стала двойной, границы пересобраны только снизу/справа. */
  @media (max-width: 991px) {
    .price-list-table { min-width: 640px; border-collapse: separate; border-spacing: 0; }
    .price-list-table th, .price-list-table td { border: 0; border-bottom: 1px solid #dee2e6; }
    .price-list-table th:not(:last-child),
    .price-list-table td:not(:last-child) { border-right: 1px solid #dee2e6; }
    .price-list-table th:first-child,
    .price-list-table td:first-child { position: sticky; left: 0; z-index: 1; }
    .price-list-table thead th:first-child { z-index: 3; }
  }
`;

// Утилита для сортировки диаметров (R12, R13, R20...)
const sortDiameters = (diameters: string[]): string[] => {
  return [...diameters].sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, ''), 10);
    const numB = parseInt(b.replace(/\D/g, ''), 10);
    return (numA || 0) - (numB || 0) || a.localeCompare(b);
  });
};

// Текущая цена позиции с учётом несохранённого черновика: черновик живёт
// в value (строка, '' = цена стёрта), после blur-сохранения price === parseFloat(value)
const currentItemPrice = (item: PriceListItem): number | null => {
  if (item.value !== undefined && item.value !== null) {
    const parsed = parseFloat(item.value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return item.price ?? null;
};

// Расхождение ячейки «Цен комплексов» со снапшотом /complexes: current — цена
// из позиции прайса (в т.ч. черновик), snapshot — цена на момент загрузки,
// draft — черновик ещё не сохранён (blur не прошёл)
interface ComplexCellChange {
  current: number | null;
  snapshot: number | null;
  draft: boolean;
}

const PriceListViewComplexTable = (props: PriceListViewComplexTableProps) => {
  const { priceListItemsStore, priceListId, vehicleTypeRefValueName, items } = props;
  const { calculateComplexTableList, priceListItems } = priceListItemsStore!;

  const [loading, setLoading] = useState(false);

  // Базис отображения цены: за комплект (4 колеса, по умолчанию) или за 1 колесо.
  // Бэк отдаёт цену за 1 колесо, множитель применяется только при показе.
  const [wheelMode, setWheelMode] = useState<'4' | '1'>('4');
  const priceMultiplier = wheelMode === '4' ? 4 : 1;

  // Планшет/телефон: колонка «Комплекс» уже (250px не влезает), матрица скроллится
  const isTabMobile = useMedia('(max-width: 991px)');

  // Контролируемый режим (передан items) — данные приходят пропсом, запрос не нужен
  const isControlled = items !== undefined;

  const loadData = useCallback(async () => {
    if (isControlled) return;
    setLoading(true);
    try {
      await priceListItemsActions.getCalculateComplexTableList(priceListId);
    } finally {
      setLoading(false);
    }
  }, [priceListId, isControlled]);

  useEffect(() => { loadData(); }, [loadData]);

  const sourceList = isControlled ? items : calculateComplexTableList;

  // Позиции прайса, являющиеся прямой ценой комплекса (механика admin.api с
  // 22.09.2026): ключ vendorCode + точная пара классификаторов диаметр × тип ТС —
  // тот же поиск, что делает бек в resolveDirectComplexPriceOrNull
  const complexItemsByKey = useMemo(() => {
    const map = new Map<string, PriceListItem>();
    priceListItems.forEach((item) => {
      if (!item.positionVendorCode || !COMPLEX_VENDOR_CODE_SET.has(item.positionVendorCode)) return;
      if (item.classifier1RefValueId == null || item.classifier2RefValueId == null) return;
      map.set(
        `${ item.positionVendorCode }__${ item.classifier1RefValueId }__${ item.classifier2RefValueId }`,
        item
      );
    });
    return map;
  }, [priceListItems]);

  // Live-расчёт изменившихся ячеек: цена комплекса = прямая строка прайса, поэтому
  // текущее значение берём из стора без запроса (включая черновик ввода). Ячейка
  // подсвечена, пока значение не вернётся к снапшоту; сам снапшот перечитывается
  // только при монтировании. Только для страницы редактирования: в контролируемом
  // режиме (просмотр прайса офиса) стор принадлежит другой странице.
  // Ключ включает тип ТС: один и тот же комплекс × диаметр есть у каждого типа ТС
  const cellChanges = useMemo(() => {
    const changes = new Map<string, ComplexCellChange>();
    if (isControlled || !sourceList) return changes;
    sourceList.forEach((row) => {
      const vendorCode = row.complexRefValueId != null
        ? COMPLEX_PRICE_VENDOR_CODES[row.complexRefValueId]
        : undefined;
      if (!vendorCode || !row.diameterRefValueName) return;
      const item = complexItemsByKey.get(
        `${ vendorCode }__${ row.diameterRefValueId }__${ row.vehicleTypeRefValueId }`
      );
      if (!item) return;
      const current = item.markedToDelete ? null : currentItemPrice(item);
      if (current === (row.price ?? null)) return;
      changes.set(
        `${ row.complexRefValueId }__${ row.vehicleTypeRefValueName ?? 'Без типа' }__${ row.diameterRefValueName }`,
        { current, snapshot: row.price ?? null, draft: item.updated === true && !item.markedToDelete }
      );
    });
    return changes;
  }, [isControlled, sourceList, complexItemsByKey]);

  // Подготовка данных: фильтрация, группировка, построение матрицы
  const tablesData = useMemo(() => {
    if (!sourceList) return [];

    // ✅ Исправлено: фильтрация по vehicleTypeRefValueName (строка), а не по ID
    const filtered = vehicleTypeRefValueName
      ? sourceList.filter(i => i.vehicleTypeRefValueName === vehicleTypeRefValueName)
      : sourceList;

    if (filtered.length === 0) return [];

    // ✅ Исправлено: Map теперь использует string как ключ (vehicleTypeRefValueName)
    const vehicleGroups = new Map<string, typeof filtered>();
    
    if (vehicleTypeRefValueName) {
      // Если передано имя типа — одна группа с этим ключом
      vehicleGroups.set(vehicleTypeRefValueName, filtered);
    } else {
      // Если не передано — группируем по каждому уникальному имени типа
      filtered.forEach(item => {
        const vName = item.vehicleTypeRefValueName ?? 'Без типа';
        if (!vehicleGroups.has(vName)) vehicleGroups.set(vName, []);
        vehicleGroups.get(vName)!.push(item);
      });
    }

    return Array.from(vehicleGroups.entries()).map(([vName, items]) => {
      const vehicleName = items[0]?.vehicleTypeRefValueName || vName;

      // Столбцы: уникальные диаметры, отсортированные
      const diameters = Array.from(new Set(items.map(i => i.diameterRefValueName).filter(Boolean) as string[]));
      const sortedDiameters = sortDiameters(diameters);

      // Строки: уникальные комплексы, отсортированные по ID
      const complexMap = new Map<number, typeof items[0]>();
      items.forEach(item => {
        if (!complexMap.has(item.complexRefValueId!)) {
          complexMap.set(item.complexRefValueId!, item);
        }
      });
      
      // Получаем массив ID в нужном порядке
      const order = Object.values(TYPE_OPERATION) as number[];
      const getOrderIndex = (id: number) => {
        const idx = order.indexOf(id);
        return idx === -1 ? Infinity : idx;
      };

      const complexes = Array.from(complexMap.values())
        .sort((a, b) => {
          const idxA = getOrderIndex(a.complexRefValueId!);
          const idxB = getOrderIndex(b.complexRefValueId!);
          if (idxA !== idxB) return idxA - idxB;
          // если оба отсутствуют в TYPE_OPERATION — сортируем по имени
          return (a.complexName || '').localeCompare(b.complexName || '');
        });

      // Map для быстрого доступа к цене: "complexId__diameterName" -> price
      const priceMap = new Map<string, number | undefined>();
      items.forEach(item => {
        priceMap.set(`${ item.complexRefValueId }__${ item.diameterRefValueName }`, item.price);
      });

      return { vehicleName, sortedDiameters, complexes, priceMap };
    });
  }, [sourceList, vehicleTypeRefValueName]);

  if (loading) {
    return <SpinnerOverlay loading={ loading } outerHeight='150px' marginBottom='0' />;
  }

  if (tablesData.length === 0) {
    return <div className='text-muted p-3'>Нет данных для отображения</div>;
  }

  return (
    <>
      <style>{ TABLE_STYLES }</style>
      { /* Заголовок секции — единый вид с «Розничными ценами» и «Комплексными ценами»
         из PriceListEditTableV3. Базис цены (4/1 колеса) задаётся сегментным переключателем
         по образцу ToggleGroup из reports (btn-group + core Button), поэтому в подсказке
         осталось только «только просмотр» */ }
      <Row className='mb-3'>
        <Col md={ 8 } className='d-flex align-items-center flex-wrap'>
          <h5 className='mb-0 font-weight-bold'>Цены комплексов</h5>
          <div className='btn-group btn-group-sm ml-3 mr-3' role='group' aria-label='Базис цены комплекса'>
            <Button
              variant='outline-primary'
              active={ wheelMode === '4' }
              text='4 колеса'
              onClick={ () => setWheelMode('4') }
            />
            <Button
              variant='outline-primary'
              active={ wheelMode === '1' }
              text='1 колесо'
              onClick={ () => setWheelMode('1') }
            />
          </div>
          { /* <span className='text-muted small'>(только просмотр)</span> */ }
        </Col>
      </Row>
      { tablesData.map((table, idx) => (
        <div key={ idx } className={ idx > 0 ? 'mt-3' : '' }>
          { tablesData.length > 1 && <h6 className='mb-2 font-weight-bold'>{ table.vehicleName }</h6> }
          <div className='table-responsive'>
            <Table className='price-list-table table-bordered table-hover'>
              <thead className='thead-light'>
                <tr>
                  { /* Геометрия как у «Розничных цен» (fixed-раскладка):
                     первый столбец 250px (на планшете/телефоне — уже), диаметры делят остаток поровну */ }
                  <th style={ { width: isTabMobile ? '130px' : '250px' } }>Комплекс</th>
                  { table.sortedDiameters.map(d => (
                    <th key={ d }>{ d }</th>
                  )) }
                </tr>
              </thead>
              <tbody>
                { table.complexes.map(comp => (
                  <tr
                    key={ comp.complexRefValueId }
                    // hover-связка с позицией в «Розничных ценах» (complexRowHoverStore)
                    className={ comp.complexRefValueId === complexRowHoverStore.linkedComplexRefValueId
                      ? 'complex-row-linked'
                      : undefined }
                  >
                    <td className='position-cell font-weight-bold'>{ comp.complexName }</td>
                    { table.sortedDiameters.map(d => {
                      const price = table.priceMap.get(`${ comp.complexRefValueId }__${ d }`);
                      const change = cellChanges.get(`${ comp.complexRefValueId }__${ table.vehicleName }__${ d }`);
                      if (change) {
                        const currentPrice = change.current != null ? change.current * priceMultiplier : null;
                        const snapshotPrice = change.snapshot != null ? change.snapshot * priceMultiplier : null;
                        return (
                          <td
                            key={ d }
                            className='calc-cell complex-price-changed'
                            title={ `Было: ${ snapshotPrice ?? '—' } → ${ change.draft ? 'станет' : 'стало' }: ${ currentPrice ?? '—' }` }
                          >
                            <span className='font-weight-bold'>{ currentPrice ?? '—' }</span>
                            { snapshotPrice != null && (
                              <s className='text-muted complex-price-old'>{ snapshotPrice }</s>
                            ) }
                          </td>
                        );
                      }
                      return (
                        <td key={ d } className='calc-cell'>
                          { price != null ? price * priceMultiplier : '—' }
                        </td>
                      );
                    }) }
                  </tr>
                )) }
              </tbody>
            </Table>
          </div>
        </div>
      )) }
    </>
  );
};

export default inject('priceListItemsStore')(observer(PriceListViewComplexTable));